import { useEffect, useState, useCallback, useRef, ReactElement } from 'react'
import axios from 'axios'
import { QRCodeSVG } from 'qrcode.react'
import Header from '../../component/Header'
import * as Styled from './Wallet.styled'
import { useAppDispatch, useAppSelector } from '../../Hooks/hooks'
import { selectUserInfo } from '../Account/accountSlice'
import { getUserInfo } from '../Account/actions'
import {
  WALLET_HISTORY_API,
  WALLET_TOPUP_STATUS_API,
  CREDIT_SETTINGS_API,
} from '../../utils/API'
import { getLocalStorage } from '../../utils/localStorage'
import MethodPicker from './MethodPicker'
import AmountInputModal from './AmountInputModal'
import CardForm from './CardForm'
import { computeCardFee } from './cardFee'

const LIFF_TOPUP_URL = 'https://liff.line.me/2009888839-40FcF4w9?action=topup'
const LIFF_HOME_URL = 'https://liff.line.me/2009888839-40FcF4w9'
const LIFF_REGISTER_URL = 'https://liff.line.me/2009888839-40FcF4w9'
const LINE_OA_URL = 'https://line.me/R/ti/p/@remedycoffee'

// Polling: 45 attempts × 2s = 90s. Bumped from plan's 30 × 2s = 60s to absorb
// typical Omise webhook latency. On timeout we go to 'processing' (indeterminate
// — no retry CTA), NOT 'rejected'. Mirrors credit a92c63d (Reviewer Blocker 1).
const POLL_MAX_ATTEMPTS = 45
const POLL_INTERVAL_MS = 2000

// Fallback if credit settings fetch fails (e.g. CORS or network) — card stays
// DISABLED in that case. Server-side check is the real moat.
const FALLBACK_CARD_MIN = 2000
const FALLBACK_CARD_MAX = 10000
const FALLBACK_PRESETS = [2000, 3000, 5000, 10000]

type Tx = {
  id: string
  type: 'deduct' | 'topup' | 'adjust'
  amount: number
  cash_portion: number
  bill_ref: string | null
  balance_after: number
  note: string | null
  created_at: string
  payment_method?: string | null
  card_last4?: string | null
  card_brand?: string | null
}

type CreditSettings = {
  card_topup_enabled: boolean
  card_topup_min: number
  card_topup_max: number
  card_topup_presets: number[]
  card_topup_fee_rate: number
  card_topup_fee_threshold: number
}

type PollState = 'idle' | 'polling' | 'processing' | 'success' | 'rejected'

type TopupResult = {
  amount: number
  balanceAfter: number
  paymentMethod?: string
  cardLast4?: string
  cardBrand?: string
  failureReason?: string
}

const Wallet = (): ReactElement => {
  const dispatch = useAppDispatch()
  const userInfo = useAppSelector(selectUserInfo)
  const [transactions, setTransactions] = useState<Tx[]>([])
  const [loadingHistory, setLoadingHistory] = useState<boolean>(false)

  // Card topup state
  const [showAmount, setShowAmount] = useState<boolean>(false)
  const [showMethod, setShowMethod] = useState<boolean>(false)
  const [showCardForm, setShowCardForm] = useState<boolean>(false)
  const [selectedAmount, setSelectedAmount] = useState<number>(0)
  const [cardEnabled, setCardEnabled] = useState<boolean>(false)
  const [cardMin, setCardMin] = useState<number>(FALLBACK_CARD_MIN)
  const [cardMax, setCardMax] = useState<number>(FALLBACK_CARD_MAX)
  const [presets, setPresets] = useState<number[]>(FALLBACK_PRESETS)
  const [feeRate, setFeeRate] = useState<number>(0)
  const [feeThreshold, setFeeThreshold] = useState<number>(0)
  const [pollState, setPollState] = useState<PollState>('idle')
  const [topupResult, setTopupResult] = useState<TopupResult | null>(null)

  const creditMember = userInfo?.data?.creditMember ?? null
  const isMember = creditMember != null
  const balance = creditMember?.balance ?? 0
  const memberName = creditMember?.name ?? ''
  const memberPhone = creditMember?.phone ?? ''

  useEffect(() => {
    if (userInfo.status === 'idle') {
      dispatch(getUserInfo())
    }
  }, [dispatch, userInfo.status])

  // Existing history fetch — untouched (revenue-critical hook contract). The
  // post-topup refetch routes through refreshHistory() below which mirrors the
  // same axios call.
  useEffect(() => {
    if (!isMember) return
    const local = getLocalStorage()
    setLoadingHistory(true)
    axios
      .get(WALLET_HISTORY_API, {
        params: { limit: 5 },
        headers: { Authorization: `Bearer ${local.login.token}` },
      })
      .then((res) => {
        setTransactions(res.data?.transactions ?? [])
      })
      .catch(() => {
        setTransactions([])
      })
      .finally(() => setLoadingHistory(false))
  }, [isMember])

  // Fetch credit settings on mount. Silent failure → card stays disabled.
  useEffect(() => {
    let cancelled = false
    axios
      .get<CreditSettings>(CREDIT_SETTINGS_API)
      .then((res) => {
        if (cancelled) return
        const s = res.data
        setCardEnabled(!!s.card_topup_enabled)
        if (typeof s.card_topup_min === 'number') setCardMin(s.card_topup_min)
        if (typeof s.card_topup_max === 'number') setCardMax(s.card_topup_max)
        if (Array.isArray(s.card_topup_presets) && s.card_topup_presets.length > 0) {
          setPresets(s.card_topup_presets)
        }
        if (typeof s.card_topup_fee_rate === 'number') setFeeRate(s.card_topup_fee_rate)
        if (typeof s.card_topup_fee_threshold === 'number')
          setFeeThreshold(s.card_topup_fee_threshold)
      })
      .catch(() => {
        // Card stays disabled — slip flow remains available.
      })
    return () => {
      cancelled = true
    }
  }, [])

  const refreshHistory = useCallback((): void => {
    const local = getLocalStorage()
    axios
      .get(WALLET_HISTORY_API, {
        params: { limit: 5 },
        headers: { Authorization: `Bearer ${local.login.token}` },
      })
      .then((res) => {
        setTransactions(res.data?.transactions ?? [])
      })
      .catch(() => {
        // Keep previous tx list on transient error.
      })
  }, [])

  // pollTopupStatus — recursive setTimeout. On verified: success state +
  // refresh userInfo + history. On rejected: rejected state. On timeout or
  // network error: 'processing' state (indeterminate — no retry CTA).
  // Mirrors credit a92c63d.
  const pollTopupStatus = useCallback(
    (topupId: string, attempts: number): void => {
      if (attempts > POLL_MAX_ATTEMPTS) {
        setPollState('processing')
        window.history.replaceState({}, '', '/wallet')
        return
      }
      const local = getLocalStorage()
      axios
        .get(WALLET_TOPUP_STATUS_API(topupId), {
          headers: { Authorization: `Bearer ${local.login.token}` },
        })
        .then((res) => {
          const d = res.data
          if (d.status === 'verified') {
            setTopupResult({
              amount: d.amount,
              balanceAfter: d.balance_after,
              paymentMethod: d.payment_method,
              cardLast4: d.card_last4,
              cardBrand: d.card_brand,
            })
            setPollState('success')
            dispatch(getUserInfo())
            refreshHistory()
            window.history.replaceState({}, '', '/wallet')
            return
          }
          if (d.status === 'rejected') {
            setTopupResult({
              amount: d.amount ?? 0,
              balanceAfter: balance,
              failureReason: d.failure_reason ?? 'Payment failed',
            })
            setPollState('rejected')
            window.history.replaceState({}, '', '/wallet')
            return
          }
          // still pending / verifying — poll again
          setTimeout(() => pollTopupStatus(topupId, attempts + 1), POLL_INTERVAL_MS)
        })
        .catch(() => {
          // Network error — indeterminate, same risk as timeout. Route to
          // processing (no retry CTA) to prevent double-charge from a retry.
          setPollState('processing')
          window.history.replaceState({}, '', '/wallet')
        })
    },
    [balance, dispatch, refreshHistory]
  )

  // On mount: pick up ?topup=pending&id=<id> (return from Omise 3DS).
  // Reviewer M1 fix: pollTopupStatus useCallback has `balance` in deps, so it
  // recreates when getUserInfo dispatch resolves and `balance` changes. The
  // mount-effect would then re-fire while ?topup=pending was still in the URL,
  // starting a SECOND parallel poll chain (harmless to money path but noisy).
  // Fix: clear URL params IMMEDIATELY on first detection + ref guard so even
  // recreated effects no-op.
  const pollStartedRef = useRef<boolean>(false)
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (pollStartedRef.current) return
    const params = new URLSearchParams(window.location.search)
    if (params.get('topup') === 'pending' && params.get('id')) {
      const id = params.get('id') as string
      pollStartedRef.current = true
      window.history.replaceState({}, '', '/wallet')
      setPollState('polling')
      pollTopupStatus(id, 0)
    }
  }, [pollTopupStatus])

  // Phase 9.D UX polish: method-first flow. Pick method BEFORE amount so the
  // amount input can show card-appropriate min/max (slip path has no amount
  // input on /wallet — opens LIFF which collects amount in LINE).
  function openTopup(): void {
    if (cardEnabled) {
      setShowMethod(true)
    } else {
      // Card disabled → only slip path → open LIFF directly (slip flow lives
      // there). User collects amount in LIFF.
      window.open(LIFF_TOPUP_URL, '_blank', 'noopener,noreferrer')
    }
  }

  function onPickCard(): void {
    setShowMethod(false)
    setShowAmount(true)
  }

  function onAmountSubmit(amount: number): void {
    setSelectedAmount(amount)
    setShowAmount(false)
    setShowCardForm(true)
  }

  function closeAll(): void {
    setShowAmount(false)
    setShowMethod(false)
    setShowCardForm(false)
  }

  function dismissResult(): void {
    setPollState('idle')
    setTopupResult(null)
  }

  const renderMemberSection = (): ReactElement => (
    <>
      <Styled.Section>
        <Styled.SectionTitle>Your Wallet</Styled.SectionTitle>
        <Styled.BalanceCard>
          <Styled.BalanceLabel>Balance</Styled.BalanceLabel>
          <Styled.BalanceValue negative={balance < 0}>
            {balance < 0
              ? `-฿${Math.abs(balance).toLocaleString()}`
              : `฿${balance.toLocaleString()}`}
          </Styled.BalanceValue>
          <Styled.MemberInfo>
            {memberName} {memberPhone && `· ${memberPhone}`}
          </Styled.MemberInfo>
        </Styled.BalanceCard>
        <Styled.ButtonRow>
          <Styled.PrimaryBtn as='button' type='button' onClick={openTopup}>
            + Top Up
          </Styled.PrimaryBtn>
          <Styled.SecondaryBtn href={LIFF_HOME_URL} target='_blank' rel='noopener noreferrer'>
            View Full History
          </Styled.SecondaryBtn>
        </Styled.ButtonRow>
      </Styled.Section>

      <Styled.Section>
        <Styled.SectionTitle>Recent Transactions</Styled.SectionTitle>
        {loadingHistory ? (
          <Styled.Spinner>Loading…</Styled.Spinner>
        ) : transactions.length === 0 ? (
          <Styled.Empty>No transactions yet — tap "Top Up" to get started</Styled.Empty>
        ) : (
          <Styled.TxList>
            {transactions.map((tx) => {
              const isTopup = tx.type === 'topup'
              const label =
                tx.type === 'topup' && tx.payment_method === 'credit_card'
                  ? 'Top Up (Card)'
                  : tx.type === 'topup'
                  ? 'Top Up'
                  : tx.type === 'deduct'
                  ? 'Payment'
                  : 'Adjustment'
              const sign = isTopup ? '+' : '-'
              const time = new Date(tx.created_at).toLocaleString('en-GB', {
                day: 'numeric',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
              })
              return (
                <Styled.TxItem key={tx.id}>
                  <Styled.TxIcon topup={isTopup}>{isTopup ? '+' : '−'}</Styled.TxIcon>
                  <Styled.TxBody>
                    <Styled.TxLabel>{label}</Styled.TxLabel>
                    <Styled.TxTime>{time}</Styled.TxTime>
                  </Styled.TxBody>
                  <Styled.TxAmount topup={isTopup}>
                    {sign}฿{Math.abs(tx.amount).toLocaleString()}
                  </Styled.TxAmount>
                </Styled.TxItem>
              )
            })}
          </Styled.TxList>
        )}
      </Styled.Section>
    </>
  )

  const renderInviteSection = (): ReactElement => (
    <Styled.InvitePanel>
      <Styled.InviteHeader>ยังไม่ได้เป็นสมาชิก Remedy Wallet</Styled.InviteHeader>
      <Styled.InviteSubheader>
        เติมก่อน — จ่ายทีหลัง
        <br />
        สะดวกกว่า ไม่ต้องพกเงินสด หรือสแกน QR ทุกครั้ง
      </Styled.InviteSubheader>

      <Styled.Divider />

      <Styled.BenefitsTitle>✨ ข้อดีของสมาชิก Wallet</Styled.BenefitsTitle>
      <Styled.BenefitsList>
        <Styled.BenefitItem>จ่ายเร็ว — เพียงแจ้งเบอร์โทรให้พนักงานหักจาก Wallet</Styled.BenefitItem>
        <Styled.BenefitItem>เช็คยอด + ประวัติได้ตลอดเวลาผ่าน LINE</Styled.BenefitItem>
        <Styled.BenefitItem>ได้รับแจ้งเตือนทุกธุรกรรมผ่าน LINE</Styled.BenefitItem>
        <Styled.BenefitItem>ใช้ได้ทั้งหน้าร้านและสั่งออนไลน์</Styled.BenefitItem>
      </Styled.BenefitsList>

      <Styled.Divider />

      <Styled.StepsTitle>📱 สมัครง่ายผ่านมือถือ</Styled.StepsTitle>
      <Styled.StepsList>
        <Styled.StepItem>
          <Styled.StepStrong>เพิ่ม Remedy เป็นเพื่อนใน LINE</Styled.StepStrong>
          <Styled.StepHint>กดปุ่มด้านล่าง หรือ เพิ่มเพื่อน @remedycoffee</Styled.StepHint>
          <Styled.CenteredBtnWrap>
            <Styled.PrimaryBtn href={LINE_OA_URL} target='_blank' rel='noopener noreferrer'>
              💬 เพิ่มเพื่อน @remedycoffee
            </Styled.PrimaryBtn>
          </Styled.CenteredBtnWrap>
        </Styled.StepItem>
        <Styled.StepItem>
          <Styled.StepStrong>
            สแกน QR ด้านล่าง หรือ{' '}
            <Styled.InlineLink href={LIFF_REGISTER_URL} target='_blank' rel='noopener noreferrer'>
              สมัครผ่านลิงค์นี้
            </Styled.InlineLink>
          </Styled.StepStrong>
          <Styled.QrPanel>
            <QRCodeSVG value={LIFF_REGISTER_URL} size={160} level='M' />
            <Styled.QrCaption>สแกนด้วยกล้องใน LINE เพื่อเปิดหน้าสมัคร</Styled.QrCaption>
          </Styled.QrPanel>
        </Styled.StepItem>
        <Styled.StepItem>
          <Styled.StepStrong>กรอกเบอร์โทร → ยืนยัน</Styled.StepStrong>
          <Styled.StepHint>ระบบจะผูกบัญชีให้อัตโนมัติ</Styled.StepHint>
        </Styled.StepItem>
        <Styled.StepItem>
          <Styled.StepStrong>เติม Wallet ครั้งแรกในแอป LINE</Styled.StepStrong>
          <Styled.StepHint>เริ่มต้นเพียง ฿100</Styled.StepHint>
        </Styled.StepItem>
      </Styled.StepsList>

      <Styled.FootnoteRow>
        หรือติดต่อพนักงานที่หน้าร้านเพื่อสมัครก็ได้
      </Styled.FootnoteRow>
    </Styled.InvitePanel>
  )

  // Card topup overlays — rendered above everything when active.
  const renderPollState = (): ReactElement | null => {
    if (pollState === 'polling') {
      return (
        <Styled.ModalOverlay>
          <Styled.ModalCard>
            <Styled.PollingCard>
              <Styled.PollingTitle>Verifying with your bank…</Styled.PollingTitle>
              <Styled.PollingHint>Please wait — usually under 90 seconds</Styled.PollingHint>
            </Styled.PollingCard>
          </Styled.ModalCard>
        </Styled.ModalOverlay>
      )
    }
    if (pollState === 'processing') {
      // Indeterminate state — NO retry CTA. A retry tap would mint a new
      // idempotency_key (different CardForm mount) and risk a second charge
      // while the first is still verifying at Omise. Webhook reconciles async.
      return (
        <Styled.ModalOverlay>
          <Styled.ModalCard>
            <Styled.ProcessingCard>
              <Styled.ProcessingTitle>Still verifying with your bank</Styled.ProcessingTitle>
              <Styled.ProcessingBox>
                Waiting for bank confirmation — <strong>do not pay again</strong>
                <br />
                Your Wallet will update automatically once confirmed
              </Styled.ProcessingBox>
              <Styled.ProcessingHint>
                Check back in 5–10 minutes
                <br />
                If it takes longer, please contact the shop
              </Styled.ProcessingHint>
              <Styled.SubmitBtn type='button' onClick={dismissResult}>
                Close
              </Styled.SubmitBtn>
            </Styled.ProcessingCard>
          </Styled.ModalCard>
        </Styled.ModalOverlay>
      )
    }
    if (pollState === 'success' && topupResult) {
      return (
        <Styled.ModalOverlay onClick={dismissResult}>
          <Styled.ModalCard onClick={(e) => e.stopPropagation()}>
            <Styled.SuccessCard>
              <Styled.SuccessTitle>Top-Up Successful</Styled.SuccessTitle>
              <Styled.SuccessAmount>
                +฿{topupResult.amount.toLocaleString()}
              </Styled.SuccessAmount>
              <Styled.SuccessBalanceBox>
                <Styled.SuccessBalanceLabel>New Balance</Styled.SuccessBalanceLabel>
                <Styled.SuccessBalanceValue negative={topupResult.balanceAfter < 0}>
                  ฿{topupResult.balanceAfter.toLocaleString()}
                </Styled.SuccessBalanceValue>
              </Styled.SuccessBalanceBox>
              <Styled.SubmitBtn type='button' onClick={dismissResult}>
                Close
              </Styled.SubmitBtn>
            </Styled.SuccessCard>
          </Styled.ModalCard>
        </Styled.ModalOverlay>
      )
    }
    if (pollState === 'rejected' && topupResult) {
      return (
        <Styled.ModalOverlay onClick={dismissResult}>
          <Styled.ModalCard onClick={(e) => e.stopPropagation()}>
            <Styled.FailCard>
              <Styled.FailTitle>Payment Failed</Styled.FailTitle>
              <Styled.FailBox>
                {topupResult.failureReason ?? 'Payment failed'}
              </Styled.FailBox>
              <Styled.FailHint>
                Your Wallet was not charged — you can try again
              </Styled.FailHint>
              <Styled.SubmitBtn type='button' onClick={dismissResult}>
                Close
              </Styled.SubmitBtn>
            </Styled.FailCard>
          </Styled.ModalCard>
        </Styled.ModalOverlay>
      )
    }
    return null
  }

  return (
    <Styled.Wallet>
      <Header text='My Wallet' />
      {userInfo.status === 'loading' ? (
        <Styled.Spinner>กำลังโหลด...</Styled.Spinner>
      ) : isMember ? (
        renderMemberSection()
      ) : (
        renderInviteSection()
      )}

      {/* Phase 9.D: AmountInputModal only renders on the card path now (slip
          goes to LIFF before any amount input on this side). Bounds always
          come from settings.card_topup_min/max + card_topup_presets. */}
      {showAmount && (
        <AmountInputModal
          min={cardMin}
          max={cardMax}
          presets={presets}
          feeRate={feeRate}
          feeThreshold={feeThreshold}
          onSubmit={onAmountSubmit}
          onCancel={closeAll}
        />
      )}

      {showMethod && (
        <MethodPicker
          cardEnabled={cardEnabled}
          cardMin={cardMin}
          onPickCard={onPickCard}
          onClose={closeAll}
        />
      )}

      {showCardForm && (
        <CardForm
          amount={selectedAmount}
          fee={computeCardFee(selectedAmount, { rate: feeRate, threshold: feeThreshold }).fee}
          onCancel={closeAll}
        />
      )}

      {renderPollState()}
    </Styled.Wallet>
  )
}

export default Wallet
